import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Compass, MapPin, Calendar, Plane, Building2 } from "lucide-react";

export default function PublicTrip() {
  const { token } = useParams();
  const [trip, setTrip] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/trips/${token}`).then(r => setTrip(r.data)).catch(() => setErr(true));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
    <div className="text-center">
      <div className="font-serif-display text-3xl">Trip not available</div>
      <div className="text-stone-500 mt-2">The link may have been disabled.</div>
    </div>
  </div>;
  if (!trip) return <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] text-stone-500 font-mono-code">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FAF7F2]/85 border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#C85A32] text-white flex items-center justify-center"><Compass size={16}/></div>
            <span className="font-serif-display text-lg font-bold">WanderLoom</span>
          </Link>
          <span className="font-mono-code text-[10px] uppercase tracking-[0.25em] text-amber-700">Public itinerary</span>
        </div>
      </header>
      <div className="relative h-72 overflow-hidden bg-stone-900">
        <img src={trip.cover_image || "https://images.unsplash.com/photo-1534445867742-43195f401b6c?crop=entropy&cs=srgb&fm=jpg&q=85"} className="w-full h-full object-cover opacity-70"/>
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 to-transparent"/>
      </div>
      <main className="max-w-4xl mx-auto px-4 -mt-32 pb-16 text-white">
        <div className="font-mono-code text-xs uppercase tracking-[0.3em] text-amber-300 mb-2">Trip journal</div>
        <h1 className="font-serif-display text-4xl sm:text-6xl font-bold leading-[1.05]">{trip.title}</h1>
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/90">
          <span className="flex items-center gap-1.5"><MapPin size={14}/> {trip.destination}</span>
          <span className="flex items-center gap-1.5"><Calendar size={14}/> {trip.days.length} days</span>
          {trip.owner?.name && <span>by {trip.owner.name}</span>}
        </div>
        {trip.description && <p className="mt-4 text-white/85 max-w-2xl">{trip.description}</p>}

        <div className="mt-10 space-y-8 text-stone-900">
          {trip.days.map(d => (
            <section key={d.day_number} className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="font-mono-code text-[10px] uppercase tracking-[0.25em] text-amber-700">Day {String(d.day_number).padStart(2,"0")}</div>
              <h2 className="font-serif-display text-2xl font-semibold mt-1">{d.theme || `Day ${d.day_number}`}</h2>
              <ol className="mt-4 space-y-3">
                {d.activities.map((a, i) => (
                  <li key={a.id} className="pl-8 relative">
                    <span className="absolute left-0 top-0.5 w-6 h-6 rounded-full bg-stone-900 text-white flex items-center justify-center text-[10px] font-mono-code">{i+1}</span>
                    <div className="flex flex-wrap items-center gap-2">
                      {a.time && <span className="font-mono-code text-xs text-stone-500">{a.time}</span>}
                      <span className="font-mono-code text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">{a.category}</span>
                    </div>
                    <div className="font-semibold mt-0.5">{a.title}</div>
                    {a.location_name && <div className="text-xs text-stone-500 flex items-center gap-1"><MapPin size={11}/>{a.location_name}</div>}
                    {a.notes && <div className="text-sm text-stone-600 mt-1">{a.notes}</div>}
                  </li>
                ))}
              </ol>
            </section>
          ))}

          {trip.flights?.length > 0 && (
            <section className="bg-white rounded-2xl border border-stone-200 p-6">
              <h2 className="font-serif-display text-2xl font-semibold flex items-center gap-2"><Plane size={20}/> Flights</h2>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {trip.flights.map(fl => (
                  <div key={fl.id} className="p-4 rounded-xl border border-stone-200">
                    <div className="font-mono-code text-xs text-sky-700">{fl.airline} · {fl.flight_no}</div>
                    <div>{fl.from_airport} → {fl.to_airport}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {trip.hotels?.length > 0 && (
            <section className="bg-white rounded-2xl border border-stone-200 p-6">
              <h2 className="font-serif-display text-2xl font-semibold flex items-center gap-2"><Building2 size={20}/> Stays</h2>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {trip.hotels.map(h => (
                  <div key={h.id} className="p-4 rounded-xl border border-stone-200">
                    <div className="font-mono-code text-xs text-emerald-700">Stay</div>
                    <div className="font-semibold">{h.name}</div>
                    <div className="text-xs text-stone-500">{h.check_in} → {h.check_out}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
